<script>
import { BACKEND_HTTP_PATH } from 'src/config';
import { toast } from 'src/utils';

export default {
  name: 'ProgressIndicator',
  props: {
    exportState: {
      type: String,
      required: true,
      validator: (value) => ['notStarted', 'inProgress', 'completed'].includes(value),
    },
    exportUrl: {
      type: String,
      default: null,
    },
    isAnotherReportEnquiredWithoutExport: {
      type: Boolean,
    },
  },
  data() {
    return {
      showMenu: false,
    };
  },
  computed: {
    handleLink() {
      if (!this.exportUrl) return this.$t('T_EXPORT_WAS_NOT_COMPLETED');
      const exportUrlParts = this.exportUrl.split('/');
      return exportUrlParts[exportUrlParts.length - 1];
    },
  },
  methods: {
    handleClickClose() {
      this.$emit('click');
    },
    copyLink() {
      if (!this.exportUrl) return;
      const exportUrl = this.exportUrl.replace(/^\//, BACKEND_HTTP_PATH);
      if (navigator?.clipboard) {
        navigator.clipboard.writeText(exportUrl).then(() => {
          toast.info({
            msg: this.$t('T_COPY_TO_LINK'),
            duration: 10000,
          });
        }).catch(() => {
          toast.error({
            msg: this.$t('T_COPY_TO_LINK_ERROR'),
            duration: 10000,
          });
        });
      } else {
        toast.error({
          msg: this.$t('T_COPY_TO_LINK_ERROR'),
          duration: 10000,
        });
      }
    },
    downloadFile() {
      const exportUrl = this.exportUrl.replace(/^\//, BACKEND_HTTP_PATH);
      window.open(exportUrl, '_blank');
    },
  },
};
</script>

<template lang="pug">
  div(v-if="exportState !== 'notStarted' && !isAnotherReportEnquiredWithoutExport" :class="$style.progressIndicatorContainer")
    div(:class="[$style.progressIndicator, $style[exportState]]" data-test="PROGRESS_INDICATOR"  @click="handleClickClose")
      template(v-if="exportState === 'notStarted'")
        ui-icon(:class="$style.icon") play
      template(v-if="exportState === 'inProgress'")
        div(:class="$style.spinner")
        span(:class="$style.close")
      div(
        v-if="exportState === 'completed'"
        :class="$style.progressIndicator"
      )
        v-menu(
          :model-value="showMenu"
          offset-y
        )
          template(v-slot:activator="{ on, attrs }")
            div(:class="$style.iconContainer" :title="$t('T_DOWNLOAD')" v-bind="attrs"
              v-on="on")
              ui-icon(:class="$style.iconItem") ok
              ui-icon(:class="$style.iconItem") dropdown
          v-list(min-width="230")
            v-tooltip(z-index="10000" bottom max-width="300px" offset content-class="tooltip" open-delay="500")
              template(v-slot:activator="{ on, attrs }")
                v-list-item(v-on="on" @click="copyLink")
                  ui-icon(:class="$style.iconPanel") copy
                  v-list-item-title {{ $t('T_COPY_LINK') }}
              div {{ handleLink }}
            v-tooltip(z-index="10000" bottom max-width="300px" offset content-class="tooltip"  open-delay="500")
              template(v-slot:activator="{ on, attrs }")
                v-list-item(v-on="on" @click="downloadFile")
                  ui-icon(:class="$style.iconPanel") download
                  v-list-item-title {{ $t('T_DOWNLOAD') }}
              div {{ handleLink }}
</template>

<style lang="sass" scoped>
.tooltip
  overflow-wrap: break-word
  word-wrap: break-word
  word-break: break-all
  word-break: break-word
  font-size: 12px
  line-height: 15px
  opacity: 1
  background-color: $color_bg_light_grey
  color: $color_text_medium_grey
  box-shadow: 0px 0px 8px -2px rgba(21, 23, 24, 0.2)
  padding: 8px 13px
  max-height: 78%
  overflow: hidden
  z-index: 10
  width: 410px
</style>

<style lang="sass" module>
.progressIndicatorContainer
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  gap: 8px
  position: relative
.progressIndicator
  position: relative
  width: 25px
  height: 25px
  border-radius: 50%
  display: flex
  align-items: center
  justify-content: center
  cursor: pointer
  user-select: none
  transition: background-color 0.3s, border-color 0.3s, transform 0.3s
  &.notStarted
    border: 2px dashed $color_grey_light
    background-color: $white
    &:hover
      transform: scale(1.05)
      border-color: $color_grey_toggle
    .icon
      font-size: 22px
      color: $color_grey_closed
  &.inProgress
    border: 2px solid $color_green
    background-color: $white
    &:hover
      transform: scale(1.05)
      border-color: $color_green
    .spinner
      position: absolute
      width: 20px
      height: 20px
      border: 3px solid transparent
      border-top: 3px solid $color_green
      border-radius: 50%
      animation: spin 0.8s linear infinite
    .icon
      font-size: 22px
      color: $color_green
@keyframes spin
  0%
    transform: rotate(0deg)
  100%
    transform: rotate(360deg)
.icon
  width: 15px
  height: 15px
.close
  position: relative
  display: inline-block
  width: 16px
  height: 16px
  cursor: pointer
  &:before, &:after
    content: ''
    position: absolute
    top: 50%
    left: 50%
    width: 12px
    height: 3px
    background-color: $color_grey_toggle
    border-radius: 1px
    transform-origin: center
    transition: transform 0.3s ease, background-color 0.3s ease
  &:before
    transform: translate(-50%, -50%) rotate(45deg)
  &:after
    transform: translate(-50%, -50%) rotate(-45deg)
  &:hover
    &:before, &:after
      background-color: $color_grey_toggle
      transform: translate(-50%, -50%) scale(1.1) rotate(45deg)
.iconPanel
  width: 24px
  height: 24px
  font-size: 18px
  color: inherit
  margin-right: 16px
  &:hover
    transform: scale(1.1)
.iconContainer
  display: flex
  border: 2px solid $color_gray_active
  border-radius: 15px
  padding: 2px
  align-items: center
  justify-content: center
  &:hover
    border: 2px solid $color_grey_light
.iconItem
  width: 22px
  height: 22px
.actionPanel
  position: absolute
  top: 33px
  right: -15px
  min-width: 160px
  max-width: 350px
  background-color: $white
  border-radius: 4px
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)
  padding: 8px 0
  z-index: 1000
  font-family: inherit
  white-space: nowrap
.actionItem
  display: flex
  align-items: center
  padding: 8px 16px
  font-size: 14px
  color: rgba(0, 0, 0, 0.87)
  cursor: pointer
  transition: background-color 0.3s cubic-bezier(0.25, 0.8, 0.5, 1)
  gap: 12px
  &:hover
    background-color: rgba(0, 0, 0, 0.04)
.menuLink
  display: flex
  align-items: center
  padding: 8px 16px
  font-size: 12px
  color: $color_text
  word-wrap: break-word
  white-space: break-spaces
</style>
